/**
 * The shared shape of every operation screen (spec sections 19-21, 57).
 *
 * Scheduling, rostering and cancelling work the same way for every operation
 * type, so they live here once. What differs per type is the *planner* — the AC
 * lane optimizer, the Swordland legion builder, the Fortress stagger — and most
 * of those do not exist yet.
 *
 * That distinction is the point of this component. An R4 can genuinely schedule
 * a Fortress tonight and record who is coming; what they cannot yet get is a
 * recommended attack order. Collapsing those into one "not available" screen
 * would hide working functionality, and pretending the planner exists would be
 * worse. So each page shows the real thing and names the missing thing.
 *
 * The roster is staffing information (spec section 50). Nothing here ranks
 * members, and the attendance labels avoid blame vocabulary in both languages.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { EventDto } from '@kso/schemas';
import {
  useAllianceMembers,
  useCancelEvent,
  useCreateEvent,
  useEvent,
  useEvents,
  useSetEventParticipants,
} from '../../api/queries';
import { useCoreContext } from '../../lib/coreAlliance';
import { Badge, Card, Notice, PageHeader, Value } from '../../components/ui/primitives';
import { EmptyState, ErrorState, QueryBoundary } from '../../components/ui/states';
import { formatDateTime } from '../../lib/format';
import { attendanceLabel, eventStatusLabel } from '../../lib/enumLabels';
import { useT } from '../../i18n';

const STATUS_TONE: Record<EventDto['status'], string> = {
  PLANNED: 'neutral',
  OPEN: 'accent',
  IN_PROGRESS: 'accent',
  COMPLETED: 'CONFIRMED',
  CANCELLED: 'UNKNOWN',
};

export function EventTypePage({
  eventType,
  title,
  subtitle,
  plannerNote,
  plannerPhase,
}: {
  eventType: EventDto['eventType'];
  title: string;
  subtitle: string;
  /** What the type-specific planner will eventually do. */
  plannerNote: string;
  /** Which spec section delivers it, so the gap is bounded. */
  plannerPhase: string;
}) {
  const t = useT();
  const core = useCoreContext();
  const events = useEvents({ eventType });
  const [openEventId, setOpenEventId] = useState<string | null>(null);

  return (
    <>
      <PageHeader title={title} subtitle={subtitle} />

      <div className="stack">
        <Notice severity="INFO">
          <strong>{t.events.plannerNotAutomated}</strong> {plannerNote}
          {t.events.plannerLands(plannerPhase)}
        </Notice>

        <CreateEventCard eventType={eventType} allianceId={core.allianceId} title={title} />

        <Card title={t.events.scheduled}>
          <QueryBoundary
            query={events}
            loadingLabel={t.events.loadingEvents}
            isEmpty={(data) => data.events.length === 0}
            empty={
              <EmptyState
                title={t.events.nothingScheduledTitle}
                body={t.events.nothingScheduledBody}
              />
            }
          >
            {(data) => (
              <EventTable
                events={data.events}
                openEventId={openEventId}
                onToggle={(id) => setOpenEventId((current) => (current === id ? null : id))}
              />
            )}
          </QueryBoundary>
        </Card>

        {openEventId ? (
          <EventDetail eventId={openEventId} onClose={() => setOpenEventId(null)} />
        ) : null}
      </div>
    </>
  );
}

function CreateEventCard({
  eventType,
  allianceId,
  title,
}: {
  eventType: EventDto['eventType'];
  allianceId: string | null;
  title: string;
}) {
  const t = useT();
  const create = useCreateEvent();
  const [name, setName] = useState('');
  const [startsAt, setStartsAt] = useState('');

  return (
    <Card title={t.events.newEvent(title)}>
      <div className="row" style={{ gap: 12, alignItems: 'flex-end' }}>
        <label className="row" style={{ gap: 6 }}>
          <span className="muted small">{t.events.name}</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t.common.optional}
            style={{ width: 220 }}
          />
        </label>
        <label className="row" style={{ gap: 6 }}>
          <span className="muted small">{t.events.starts}</span>
          <input
            type="datetime-local"
            value={startsAt}
            onChange={(event) => setStartsAt(event.target.value)}
          />
        </label>
        <button
          type="button"
          className="btn btn--primary"
          disabled={create.isPending}
          onClick={() =>
            create.mutate(
              {
                eventType,
                ...(allianceId ? { allianceId } : {}),
                ...(name.trim() ? { name: name.trim() } : {}),
                ...(startsAt ? { startsAt: new Date(startsAt).toISOString() } : {}),
              },
              {
                onSuccess: () => {
                  setName('');
                  setStartsAt('');
                },
              },
            )
          }
        >
          {create.isPending ? t.events.scheduling : t.events.schedule}
        </button>
      </div>

      <p className="faint small" style={{ marginBottom: 0, marginTop: 8 }}>
        {t.events.optionalFieldsNote}
      </p>

      {create.error ? (
        <div style={{ marginTop: 10 }}>
          <ErrorState error={create.error} />
        </div>
      ) : null}
    </Card>
  );
}

function EventTable({
  events,
  openEventId,
  onToggle,
}: {
  events: EventDto[];
  openEventId: string | null;
  onToggle: (id: string) => void;
}) {
  const t = useT();
  const cancel = useCancelEvent();

  return (
    <>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>{t.events.columns.name}</th>
              <th>{t.events.columns.starts}</th>
              <th>{t.events.columns.status}</th>
              <th>{t.events.columns.created}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.id}>
                <td>
                  <Value absent={t.common.untitled}>{event.name}</Value>
                </td>
                <td className="nowrap">
                  <Value absent={t.events.notScheduled}>{formatDateTime(event.startsAt)}</Value>
                </td>
                <td>
                  <Badge tone={STATUS_TONE[event.status]}>
                    {eventStatusLabel(t, event.status)}
                  </Badge>
                </td>
                <td className="nowrap faint small">{formatDateTime(event.createdAt)}</td>
                <td>
                  <span className="row" style={{ gap: 4 }}>
                    <button
                      type="button"
                      className="btn btn--sm"
                      onClick={() => onToggle(event.id)}
                    >
                      {openEventId === event.id ? t.common.hide : t.events.roster}
                    </button>
                    {event.status === 'CANCELLED' ? null : (
                      <button
                        type="button"
                        className="btn btn--sm"
                        disabled={cancel.isPending}
                        // "Cancel", not "Delete": the route sets the status to
                        // CANCELLED and keeps the row. Spec section 1.4 makes
                        // history first class, and an operation that was called
                        // off is itself worth knowing about later.
                        title={t.events.cancelTitle}
                        onClick={() => cancel.mutate(event.id)}
                      >
                        {t.common.cancel}
                      </button>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {cancel.error ? (
        <div style={{ marginTop: 10 }}>
          <ErrorState error={cancel.error} />
        </div>
      ) : null}

      <p className="faint small" style={{ marginBottom: 0, marginTop: 8 }}>
        {t.events.cancelNote}
      </p>
    </>
  );
}

function EventDetail({ eventId, onClose }: { eventId: string; onClose: () => void }) {
  const t = useT();
  const detail = useEvent(eventId);
  const core = useCoreContext();
  const members = useAllianceMembers(core.allianceId ?? undefined);
  const setParticipants = useSetEventParticipants(eventId);

  const [selected, setSelected] = useState<Set<string> | null>(null);

  // Seeded from the server on first load, then owned locally while editing.
  const roster = useMemo(() => {
    if (selected) return selected;
    return new Set((detail.data?.participants ?? []).map((participant) => participant.playerId));
  }, [selected, detail.data]);

  return (
    <QueryBoundary query={detail} loadingLabel={t.events.loadingRoster}>
      {(data) => (
        <Card
          title={
            <span className="row">
              {t.events.rosterFor} <Value absent={t.common.untitled}>{data.event.name}</Value>
              <Badge tone={STATUS_TONE[data.event.status]}>
                {eventStatusLabel(t, data.event.status)}
              </Badge>
            </span>
          }
          actions={
            <button type="button" className="btn btn--sm btn--subtle" onClick={onClose}>
              {t.common.close}
            </button>
          }
        >
          {data.event.status === 'CANCELLED' ? (
            <div style={{ marginBottom: 10 }}>
              <Notice severity="INFO">{t.events.cancelledNotice}</Notice>
            </div>
          ) : null}

          <p className="muted small" style={{ marginTop: 0 }}>
            {t.events.rosterBlurb}
          </p>

          <QueryBoundary
            query={members}
            loadingLabel={t.events.loadingAllianceRoster}
            isEmpty={(members_) => members_.members.length === 0}
            empty={
              <EmptyState
                title={t.events.noAllianceRosterTitle}
                body={t.events.noAllianceRosterBody}
              />
            }
          >
            {(memberData) => (
              <>
                <div className="row" style={{ gap: 6 }}>
                  {[...memberData.members]
                    .sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id))
                    .map((member) => {
                      const isIn = roster.has(member.id);
                      return (
                        <button
                          key={member.id}
                          type="button"
                          className={isIn ? 'btn btn--sm btn--primary' : 'btn btn--sm'}
                          onClick={() =>
                            setSelected(() => {
                              const next = new Set(roster);
                              if (isIn) next.delete(member.id);
                              else next.add(member.id);
                              return next;
                            })
                          }
                        >
                          {member.name ?? member.id}
                        </button>
                      );
                    })}
                </div>

                <div className="row" style={{ marginTop: 12 }}>
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={roster.size === 0 || setParticipants.isPending}
                    onClick={() =>
                      setParticipants.mutate(
                        [...roster].map((playerId) => ({ playerId, available: true })),
                        { onSuccess: () => setSelected(null) },
                      )
                    }
                  >
                    {setParticipants.isPending
                      ? t.common.saving
                      : t.events.saveParticipants(roster.size)}
                  </button>
                  {roster.size === 0 ? (
                    <span className="muted small">{t.events.selectAtLeastOne}</span>
                  ) : null}
                  {setParticipants.data ? (
                    <span style={{ color: 'var(--high)' }}>
                      {t.events.savedCount(setParticipants.data.participantCount)}
                    </span>
                  ) : null}
                </div>

                {setParticipants.error ? (
                  <div style={{ marginTop: 10 }}>
                    <ErrorState error={setParticipants.error} />
                  </div>
                ) : null}
              </>
            )}
          </QueryBoundary>

          {data.participants.length > 0 ? (
            <div style={{ marginTop: 14 }}>
              <h3 className="muted small">{t.events.recordedParticipants}</h3>
              <div className="table-wrap" style={{ marginTop: 6 }}>
                <table className="data">
                  <thead>
                    <tr>
                      <th>{t.events.participantColumns.player}</th>
                      <th>{t.events.participantColumns.role}</th>
                      <th>{t.events.participantColumns.team}</th>
                      <th>{t.events.participantColumns.attendance}</th>
                      <th>{t.events.participantColumns.notes}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.participants.map((participant) => (
                      <tr key={participant.playerId}>
                        <td>
                          <Link to={`/players/${encodeURIComponent(participant.playerId)}`}>
                            <Value absent={t.common.unnamedPlayer}>
                              {participant.playerName}
                            </Value>
                          </Link>
                        </td>
                        <td>
                          <Value absent={t.events.unassigned}>{participant.role}</Value>
                        </td>
                        <td>
                          <Value absent={t.common.dash}>{participant.team}</Value>
                        </td>
                        <td>
                          {participant.attendanceStatus === null ? (
                            <span className="absent">{t.common.notRecorded}</span>
                          ) : (
                            attendanceLabel(t, participant.attendanceStatus)
                          )}
                        </td>
                        <td className="small">
                          <Value absent={t.common.dash}>{participant.manualNotes}</Value>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </Card>
      )}
    </QueryBoundary>
  );
}
